/**
 * audio.ts — Opus capture/encode (publisher) + decode/playback (subscriber),
 * via WebCodecs AudioEncoder/AudioDecoder. Audio rides the same binary WS lane
 * as video, tagged TRACK_AUDIO. A/V sync is timestamp-driven (best-effort: we
 * schedule decoded audio on the AudioContext clock with a small jitter lead).
 */
import { encodeFrame, payloadOf, decodeHeader, TRACK_AUDIO, TYPE_KEY, TYPE_DELTA } from './protocol.js';

const SAMPLE_RATE = 48000;
const CHANNELS = 1;
const OPUS_BITRATE = 32_000;

// ---------------------------------------------------------------------------
// Publisher: mic track → AudioEncoder(opus) → binary frames
// ---------------------------------------------------------------------------
export class AudioPublisher {
    private encoder: AudioEncoder | null = null;
    private reader: ReadableStreamDefaultReader<AudioData> | null = null;
    private stopped = false;

    constructor(
        private readonly streamId: number,
        private readonly track: MediaStreamTrack,
        private readonly sendBinary: (data: Uint8Array) => void,
    ) {}

    async start(): Promise<void> {
        this.encoder = new AudioEncoder({
            output: (chunk) => this.onChunk(chunk),
            error: (e) => console.error('[vidchat] audio encoder error', e),
        });
        this.encoder.configure({
            codec: 'opus',
            sampleRate: SAMPLE_RATE,
            numberOfChannels: CHANNELS,
            bitrate: OPUS_BITRATE,
        });
        const processor = new MediaStreamTrackProcessor<AudioData>({ track: this.track });
        this.reader = processor.readable.getReader();
        void this.pump();
    }

    private async pump(): Promise<void> {
        const reader = this.reader, encoder = this.encoder;
        if (!reader || !encoder) return;
        try {
            while (!this.stopped) {
                const { value: data, done } = await reader.read();
                if (done) break;
                if (!data) continue;
                if (encoder.state === 'configured' && encoder.encodeQueueSize < 8) {
                    try { encoder.encode(data); } catch (e) { console.error('[vidchat] audio encode threw', e); }
                }
                data.close();
            }
        } catch (e) { if (!this.stopped) console.error('[vidchat] audio pump error', e); }
    }

    private onChunk(chunk: EncodedAudioChunk): void {
        const buf = new Uint8Array(chunk.byteLength);
        chunk.copyTo(buf);
        const frame = encodeFrame(
            { streamId: this.streamId, track: TRACK_AUDIO, type: chunk.type === 'key' ? TYPE_KEY : TYPE_DELTA, timestamp: chunk.timestamp },
            buf,
        );
        this.sendBinary(frame);
    }

    stop(): void {
        this.stopped = true;
        try { this.reader?.cancel(); } catch { /* ignore */ }
        this.reader = null;
        try { if (this.encoder && this.encoder.state !== 'closed') this.encoder.close(); } catch { /* ignore */ }
        this.encoder = null;
    }
}

// ---------------------------------------------------------------------------
// Player: binary frames → AudioDecoder(opus) → scheduled AudioContext playback
// ---------------------------------------------------------------------------
export class AudioPlayer {
    private ctx: AudioContext | null = null;
    private gain: GainNode | null = null;
    private decoder: AudioDecoder | null = null;
    private playhead = 0;        // next scheduled start time (ctx clock)
    private readonly leadSec = 0.12; // jitter buffer lead
    private stopped = false;
    private muted = false;

    start(): void {
        this.ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
        this.gain = this.ctx.createGain();
        this.gain.gain.value = this.muted ? 0 : 1;
        this.gain.connect(this.ctx.destination);
        this.decoder = new AudioDecoder({
            output: (data) => this.onDecoded(data),
            error: (e) => console.error('[vidchat] audio decoder error', e),
        });
        this.decoder.configure({ codec: 'opus', sampleRate: SAMPLE_RATE, numberOfChannels: CHANNELS });
    }

    /** Local mute — gain 0/1. Never affects the publisher or other viewers. */
    setMuted(muted: boolean): void {
        this.muted = muted;
        if (this.gain) this.gain.gain.value = muted ? 0 : 1;
    }

    feed(frame: Uint8Array): void {
        if (this.stopped || !this.decoder) return;
        const h = decodeHeader(frame);
        if (!h || h.track !== TRACK_AUDIO) return;
        const bytes = payloadOf(frame).slice();
        try {
            const chunk = new EncodedAudioChunk({ type: h.type === TYPE_KEY ? 'key' : 'delta', timestamp: h.timestamp, data: bytes });
            if (this.decoder.state === 'configured') this.decoder.decode(chunk);
        } catch (e) { console.error('[vidchat] audio decode threw', e); }
        // browsers gate AudioContext until a user gesture; resume opportunistically
        if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
    }

    private onDecoded(data: AudioData): void {
        const ctx = this.ctx;
        if (this.stopped || !ctx) { data.close(); return; }
        const frames = data.numberOfFrames;
        const channels = Math.min(data.numberOfChannels, CHANNELS);
        const buffer = ctx.createBuffer(channels, frames, data.sampleRate || SAMPLE_RATE);
        for (let ch = 0; ch < channels; ch++) {
            const dest = new Float32Array(frames);
            try { data.copyTo(dest, { planeIndex: ch, format: 'f32-planar' }); }
            catch { /* some impls give interleaved; fall back to plane 0 */ try { data.copyTo(dest, { planeIndex: 0 }); } catch { /* skip */ } }
            buffer.copyToChannel(dest, ch);
        }
        data.close();

        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(this.gain ?? ctx.destination);
        const now = ctx.currentTime;
        if (this.playhead < now + 0.01) this.playhead = now + this.leadSec; // (re)prime after underrun
        src.start(this.playhead);
        this.playhead += buffer.duration;
    }

    stop(): void {
        this.stopped = true;
        try { if (this.decoder && this.decoder.state !== 'closed') this.decoder.close(); } catch { /* ignore */ }
        this.decoder = null;
        try { void this.ctx?.close(); } catch { /* ignore */ }
        this.ctx = null;
    }
}
